import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Info,
  Link2,
  MoreVertical,
  Pencil,
  Phone,
  Plus,
  Power,
  RotateCw,
  Trash2,
  Unlink,
  UserPlus,
} from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  LINES_KEY,
  useAdoptLineMutation,
  useCreateLineMutation,
  useDeleteLineMutation,
  useLineConnectQuery,
  useLineSettingsQuery,
  useLineStateQuery,
  useLinesQuery,
  useLogoutLineMutation,
  useRestartLineMutation,
  useSaveLineProfileMutation,
  useSaveLineSettingsMutation,
  useUpdateLineMutation,
} from '@/client/api/whatsapp/lines';
import { INSTANCE_ID_PATTERN } from '@/client/types/whatsapp/lines';
import type { LineSettings, WhatsAppLine } from '@/client/types/whatsapp/lines';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { getApiErrorField } from '@/client/utils/errors';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import ConfirmDeleteDialog from '@/client/components/admin/WhatsApp/ConfirmDeleteDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import SendTab from './lines/SendTab';
import ChatsTab from './lines/ChatsTab';
import GroupsTab from './lines/GroupsTab';
import LabelsTab from './lines/LabelsTab';
import PrivacyTab from './lines/PrivacyTab';
import BusinessTab from './lines/BusinessTab';
import ProxyTab from './lines/ProxyTab';
import ServerPanel from './lines/ServerPanel';
import { TabBar } from './lines/shared';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';

// ---------------------------------------------------------------------------
// helpers

/** Readable text for any error-ish value (Evolution bodies come in many shapes). */
const errText = (e: unknown): string => {
  if (e == null) return '';
  if (typeof e === 'string') return e;
  if (Array.isArray(e)) return e.map(errText).filter(Boolean).join('; ');
  if (typeof e === 'object') {
    const o = e as Record<string, unknown>;
    const inner = o.response ?? o.message ?? o.detail ?? o.error;
    if (inner != null && inner !== e) return errText(inner);
    try {
      return JSON.stringify(e);
    } catch {
      return String(e);
    }
  }
  return String(e);
};

/** Message for a toast: Evolution's `detail` when present, else the thrown message. */
const toastError = (error: unknown, fallback: string) => {
  const detail = getApiErrorField<unknown>(error, 'detail');
  const text = errText(detail) || (error as Error)?.message || fallback;
  toast.error(text);
};

const formatPhone = (digits?: string) => {
  if (!digits) return null;
  if (digits.startsWith('233') && digits.length === 12) {
    return `+233 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  return `+${digits}`;
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/, '');

const lineName = (l: WhatsAppLine) => l.label || l.instance;

const statusOf = (l: WhatsAppLine): { text: string; dot: string; tone: string } => {
  if (!l.registered) return { text: 'Not registered', dot: 'bg-gray-400', tone: 'text-gray-600' };
  if (!l.exists) return { text: 'Missing on server', dot: 'bg-red-500', tone: 'text-red-700' };
  switch (l.state) {
    case 'open':
      return { text: 'Connected', dot: 'bg-green-500', tone: 'text-green-700' };
    case 'connecting':
      return { text: 'Connecting', dot: 'bg-amber-500', tone: 'text-amber-700' };
    case 'close':
      return { text: 'Disconnected', dot: 'bg-red-500', tone: 'text-red-700' };
    case 'logged_out':
      return { text: 'Unlinked', dot: 'bg-red-500', tone: 'text-red-700' };
    default:
      return { text: 'Unknown', dot: 'bg-gray-400', tone: 'text-gray-600' };
  }
};

const StatusPill: React.FC<{ line: WhatsAppLine }> = ({ line }) => {
  const s = statusOf(line);
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${s.tone}`}>
      <span className={`h-2 w-2 rounded-full ${s.dot}`} />
      {s.text}
    </span>
  );
};

const Avatar: React.FC<{ line: WhatsAppLine; size?: number }> = ({ line, size = 36 }) =>
  line.profile_pic_url ? (
    <img
      src={line.profile_pic_url}
      alt=""
      style={{ width: size, height: size }}
      className="rounded-full object-cover bg-gray-100 flex-shrink-0"
    />
  ) : (
    <div
      style={{ width: size, height: size }}
      className="rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"
    >
      <Phone className="w-4 h-4 text-gray-400" />
    </div>
  );

// ---------------------------------------------------------------------------
// Add line

const DEFAULT_ADD_SETTINGS: Partial<LineSettings> = {
  rejectCall: true,
  groupsIgnore: true,
  alwaysOnline: false,
  readMessages: false,
  readStatus: false,
  syncFullHistory: false,
};

const SETTING_TOGGLES: { key: Exclude<keyof LineSettings, 'msgCall'>; label: string; hint: string }[] = [
  { key: 'rejectCall', label: 'Reject calls', hint: 'Decline incoming WhatsApp calls on this phone.' },
  { key: 'groupsIgnore', label: 'Ignore groups', hint: 'Do not receive group messages.' },
  { key: 'alwaysOnline', label: 'Always online', hint: 'Show the phone as online all the time.' },
  { key: 'readMessages', label: 'Mark messages read', hint: 'Send read receipts for incoming messages.' },
  { key: 'readStatus', label: 'Mark statuses read', hint: 'View other people\'s statuses automatically.' },
  { key: 'syncFullHistory', label: 'Sync full history', hint: 'Pull the whole chat history when linking.' },
];

const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}> = ({ checked, onChange, label, hint, disabled }) => (
  <label className={`flex items-start gap-3 py-1.5 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
    <input
      type="checkbox"
      className="mt-1 h-4 w-4 accent-green-600"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span>
      <span className="block text-sm font-medium text-gray-900">{label}</span>
      {hint && <span className="block text-xs text-gray-500">{hint}</span>}
    </span>
  </label>
);

const AddLineDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: (instance: string, label: string) => void;
}> = ({ open, onClose, onCreated }) => {
  const create = useCreateLineMutation();
  const [label, setLabel] = useState('');
  const [instance, setInstance] = useState('');
  const [touched, setTouched] = useState(false);
  const [settings, setSettings] = useState<Partial<LineSettings>>(DEFAULT_ADD_SETTINGS);

  useEffect(() => {
    if (open) {
      setLabel('');
      setInstance('');
      setTouched(false);
      setSettings(DEFAULT_ADD_SETTINGS);
    }
  }, [open]);

  const idOk = INSTANCE_ID_PATTERN.test(instance);
  const canSubmit = label.trim().length > 0 && idOk && !create.isPending;

  const submit = () => {
    create.mutate(
      { instance, label: label.trim(), settings },
      {
        onSuccess: () => {
          toast.success('Line added');
          onCreated(instance, label.trim());
        },
        onError: (e) => {
          const code = (e as { data?: { data?: { error?: string }; error?: string } })?.data;
          if (code?.error === 'line_exists' || code?.data?.error === 'line_exists') {
            toast.error(`A line with the id "${instance}" already exists`);
          } else {
            toastError(e, 'Could not add the line');
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !create.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add line</DialogTitle>
          <DialogDescription>Create a line, then link a phone to it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="line-label">Label</Label>
            <Input
              id="line-label"
              value={label}
              placeholder="Support phone"
              onChange={(e) => {
                setLabel(e.target.value);
                if (!touched) setInstance(slugify(e.target.value));
              }}
            />
          </div>
          <div>
            <Label htmlFor="line-instance">Instance id</Label>
            <Input
              id="line-instance"
              value={instance}
              className="font-mono"
              onChange={(e) => {
                setTouched(true);
                setInstance(e.target.value);
              }}
            />
            <p className={`text-xs mt-1 ${instance && !idOk ? 'text-red-600' : 'text-gray-500'}`}>
              2 to 32 characters: lowercase letters, digits and hyphens, starting with a letter or digit. Cannot be
              changed later.
            </p>
          </div>
          <details className="rounded-lg border border-gray-200 px-3 py-2">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">Advanced</summary>
            <div className="mt-2">
              {SETTING_TOGGLES.map((t) => (
                <Toggle
                  key={t.key}
                  label={t.label}
                  hint={t.hint}
                  checked={!!settings[t.key]}
                  onChange={(v) => setSettings((s) => ({ ...s, [t.key]: v }))}
                />
              ))}
            </div>
          </details>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {create.isPending ? 'Adding...' : 'Add line'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Rename / adopt

const LabelDialog: React.FC<{
  line: WhatsAppLine | null;
  mode: 'rename' | 'adopt';
  onClose: () => void;
  onDone: () => void;
}> = ({ line, mode, onClose, onDone }) => {
  const update = useUpdateLineMutation();
  const adopt = useAdoptLineMutation();
  const [label, setLabel] = useState('');
  const pending = update.isPending || adopt.isPending;

  useEffect(() => {
    if (line) setLabel(line.label || '');
  }, [line]);

  const submit = () => {
    if (!line) return;
    const opts = {
      onSuccess: () => {
        toast.success(mode === 'adopt' ? 'Line registered' : 'Line renamed');
        onDone();
      },
      onError: (e: Error) => toastError(e, 'Could not save the label'),
    };
    if (mode === 'adopt') adopt.mutate({ instance: line.instance, label: label.trim() }, opts);
    else update.mutate({ instance: line.instance, label: label.trim() }, opts);
  };

  return (
    <Dialog open={!!line} onOpenChange={(o) => !o && !pending && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{mode === 'adopt' ? 'Register line' : 'Rename line'}</DialogTitle>
          <DialogDescription>
            {mode === 'adopt'
              ? `"${line?.instance}" exists on the Evolution server but is not registered here. Give it a label to register it.`
              : `Instance id: ${line?.instance}`}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="line-rename">Label</Label>
          <Input id="line-rename" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !label.trim()}>
            {pending ? 'Saving...' : mode === 'adopt' ? 'Register' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Link phone

const LinkDialog: React.FC<{
  line: WhatsAppLine | null;
  onClose: () => void;
  onLinked: () => void;
}> = ({ line, onClose, onLinked }) => {
  const instance = line?.instance ?? '';
  const open = !!line;
  const [tab, setTab] = useState<'qr' | 'pairing'>('qr');
  const [phone, setPhone] = useState('');
  const [pairPhone, setPairPhone] = useState<string | null>(null);

  useEffect(() => {
    if (line) {
      setTab('qr');
      setPhone(line.phone ? `+${line.phone}` : '');
      setPairPhone(null);
    }
  }, [line?.instance]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling only runs while the dialog is open, so it stops on close.
  const qr = useLineConnectQuery(instance, null, open && tab === 'qr');
  const pairing = useLineConnectQuery(instance, pairPhone, open && tab === 'pairing' && !!pairPhone);
  const state = useLineStateQuery(instance, open);

  const current = state.data?.data?.state;
  const connected = current === 'open' || qr.data?.data?.state === 'open';
  useEffect(() => {
    if (open && connected) {
      toast.success('Linked');
      onLinked();
    }
  }, [open, connected]); // eslint-disable-line react-hooks/exhaustive-deps

  const digits = phone.replace(/\D/g, '');
  const phoneOk = digits.length >= 8 && digits.length <= 15;
  const code = pairing.data?.data?.pairing_code;
  const qrImg = qr.data?.data?.qr;

  const tabBtn = (id: 'qr' | 'pairing', text: string) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
        tab === id ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-800'
      }`}
    >
      {text}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Link phone{line ? `: ${lineName(line)}` : ''}</DialogTitle>
          <DialogDescription>
            On the phone: WhatsApp &gt; Settings &gt; Linked devices &gt; Link a device, then scan.
          </DialogDescription>
        </DialogHeader>

        <div className="flex border-b border-gray-200">
          {tabBtn('qr', 'QR code')}
          {tabBtn('pairing', 'Pairing code')}
        </div>

        {tab === 'qr' && (
          <div className="flex flex-col items-center gap-2 py-2">
            {qr.isLoading && <p className="text-sm text-gray-500">Loading QR code...</p>}
            {qr.isError && <p className="text-sm text-red-600">{(qr.error as Error).message}</p>}
            {!qr.isLoading && !qr.isError && qrImg && (
              <img src={qrImg} alt="WhatsApp link QR code" className="w-64 h-64 border border-gray-200 rounded-lg" />
            )}
            {!qr.isLoading && !qr.isError && !qrImg && (
              <p className="text-sm text-gray-500">Waiting for the server to produce a QR code...</p>
            )}
            <p className="text-xs text-gray-500">The code refreshes by itself every 20 seconds.</p>
          </div>
        )}

        {tab === 'pairing' && (
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600">
              Or on the phone: WhatsApp &gt; Settings &gt; Linked devices &gt; Link a device &gt; Link with phone number
              instead, then type the code below.
            </p>
            <div className="flex gap-2">
              <Input
                value={phone}
                placeholder="233XXXXXXXXX"
                onChange={(e) => {
                  setPhone(e.target.value);
                  setPairPhone(null);
                }}
              />
              <Button onClick={() => setPairPhone(digits)} disabled={!phoneOk || pairing.isFetching}>
                {pairing.isFetching ? 'Requesting...' : 'Get code'}
              </Button>
            </div>
            {pairing.isError && <p className="text-sm text-red-600">{(pairing.error as Error).message}</p>}
            {code && (
              <div className="text-center rounded-lg bg-gray-50 border border-gray-200 py-4">
                <div className="text-3xl font-mono font-bold tracking-widest text-gray-900">
                  {code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code}
                </div>
                <p className="text-xs text-gray-500 mt-1">Enter this code on the phone.</p>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-gray-500">Waiting for the phone to link. This closes by itself when it does.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Details drawer

const DetailsDrawer: React.FC<{
  line: WhatsAppLine | null;
  canManage: boolean;
  onClose: () => void;
  onLink: (l: WhatsAppLine) => void;
  onChanged: () => void;
}> = ({ line, canManage, onClose, onLink, onChanged }) => {
  const live = !!line && line.registered && line.exists;
  const settingsQ = useLineSettingsQuery(line?.instance ?? '', live);
  const saveSettings = useSaveLineSettingsMutation();
  const saveProfile = useSaveLineProfileMutation();
  const [settings, setSettings] = useState<LineSettings | null>(null);
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const [picture, setPicture] = useState('');
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    setTab('overview');
    setSettings(null);
    setName(line?.profile_name || '');
    setStatus('');
    setPicture('');
  }, [line?.instance]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (settingsQ.data?.data) setSettings(settingsQ.data.data);
  }, [settingsQ.data]);

  const pictureOk = !picture || /^https:\/\//i.test(picture);

  const submitSettings = () => {
    if (!line || !settings) return;
    const { msgCall, ...rest } = settings;
    saveSettings.mutate(
      { instance: line.instance, ...rest, ...(settings.rejectCall ? { msgCall } : {}) },
      {
        onSuccess: (res) => {
          toast.success('Settings saved');
          if (res?.data) setSettings(res.data);
        },
        onError: (e) => toastError(e, 'Could not save the settings'),
      },
    );
  };

  const submitProfile = () => {
    if (!line) return;
    const body: { instance: string; name?: string; status?: string; picture_url?: string } = {
      instance: line.instance,
    };
    if (name.trim()) body.name = name.trim();
    if (status.trim()) body.status = status.trim();
    if (picture.trim()) body.picture_url = picture.trim();
    if (Object.keys(body).length === 1) {
      toast.error('Fill in at least one profile field');
      return;
    }
    saveProfile.mutate(body, {
      onSuccess: () => {
        toast.success('Profile updated');
        setStatus('');
        setPicture('');
        onChanged();
      },
      onError: (e) => toastError(e, 'Could not update the profile'),
    });
  };

  return (
    <Sheet open={!!line} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex h-full w-[420px] flex-col overflow-y-auto border-l bg-background p-0 shadow-2xl sm:w-[640px]">
        {line && (
          <>
            <div className="border-b p-6">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-3">
                  <Avatar line={line} size={40} />
                  {lineName(line)}
                </SheetTitle>
                <SheetDescription className="font-mono">{line.instance}</SheetDescription>
              </SheetHeader>
            </div>

            {live && (
              <TabBar
                value={tab}
                onChange={setTab}
                tabs={[
                  { id: 'overview', label: 'Overview' },
                  { id: 'send', label: 'Send' },
                  { id: 'chats', label: 'Chats' },
                  { id: 'groups', label: 'Groups' },
                  { id: 'labels', label: 'Labels' },
                  { id: 'privacy', label: 'Privacy' },
                  { id: 'business', label: 'Business' },
                  { id: 'proxy', label: 'Proxy' },
                ]}
              />
            )}

            {live && tab !== 'overview' && (
              <div className="flex-1 p-6">
                {tab === 'send' && <SendTab instance={line.instance} connected={line.state === 'open'} />}
                {tab === 'chats' && <ChatsTab instance={line.instance} connected={line.state === 'open'} />}
                {tab === 'groups' && <GroupsTab instance={line.instance} connected={line.state === 'open'} />}
                {tab === 'labels' && <LabelsTab instance={line.instance} connected={line.state === 'open'} />}
                {tab === 'privacy' && <PrivacyTab instance={line.instance} connected={line.state === 'open'} />}
                {tab === 'business' && <BusinessTab instance={line.instance} connected={line.state === 'open'} />}
                {tab === 'proxy' && <ProxyTab instance={line.instance} />}
              </div>
            )}

            {(tab === 'overview' || !live) && (
            <div className="flex-1 space-y-6 p-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase text-gray-500 mb-1">State</div>
                  <StatusPill line={line} />
                  {line.state_at && (
                    <div className="text-xs text-gray-500 mt-0.5">since {moment.unix(line.state_at).fromNow()}</div>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500 mb-1">Phone</div>
                  <div className="text-gray-900">{formatPhone(line.phone) || 'Not linked'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500 mb-1">Sending</div>
                  <div className="text-gray-900">{line.enabled ? 'On' : 'Off'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500 mb-1">Profile name</div>
                  <div className="text-gray-900">{line.profile_name || 'None'}</div>
                </div>
                {line.disconnected_at && (
                  <div className="col-span-2 text-xs text-gray-500">
                    Last disconnected {line.disconnected_at}
                    {line.disconnect_reason != null && ` (reason ${line.disconnect_reason})`}
                  </div>
                )}
                {line.created_by && (
                  <div className="col-span-2 text-xs text-gray-500">
                    Added by {line.created_by}
                    {line.created_at ? ` ${moment.unix(line.created_at).fromNow()}` : ''}
                  </div>
                )}
              </div>

              {live && line.state !== 'open' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm text-amber-900 mb-3">
                    No phone is linked to this line, so it cannot send or receive.
                  </p>
                  <Button size="sm" onClick={() => onLink(line)} disabled={!canManage}>
                    <Link2 /> Link phone
                  </Button>
                </div>
              )}

              {!live && (
                <p className="text-sm text-gray-600">
                  {line.registered
                    ? 'This line is missing on the Evolution server. Delete it to clear the entry.'
                    : 'This instance is not registered here. Register it from the row menu to manage it.'}
                </p>
              )}

              {live && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Settings</h3>
                  {settingsQ.isLoading && <p className="text-sm text-gray-500">Loading line settings...</p>}
                  {settingsQ.isError && (
                    <p className="text-sm text-red-600">{(settingsQ.error as Error).message}</p>
                  )}
                  {settings && (
                    <div>
                      {SETTING_TOGGLES.map((t) => (
                        <Toggle
                          key={t.key}
                          label={t.label}
                          hint={t.hint}
                          disabled={!canManage}
                          checked={settings[t.key]}
                          onChange={(v) => setSettings({ ...settings, [t.key]: v })}
                        />
                      ))}
                      {settings.rejectCall && (
                        <div className="mt-2">
                          <Label htmlFor="msg-call">Message sent to callers</Label>
                          <Input
                            id="msg-call"
                            value={settings.msgCall}
                            disabled={!canManage}
                            onChange={(e) => setSettings({ ...settings, msgCall: e.target.value })}
                          />
                        </div>
                      )}
                      {canManage && (
                        <Button className="mt-3" size="sm" onClick={submitSettings} disabled={saveSettings.isPending}>
                          {saveSettings.isPending ? 'Saving...' : 'Save settings'}
                        </Button>
                      )}
                    </div>
                  )}
                </section>
              )}

              {live && line.state === 'open' && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Profile</h3>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="pf-name">Name</Label>
                      <Input id="pf-name" value={name} disabled={!canManage} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="pf-status">Status text</Label>
                      <Input
                        id="pf-status"
                        value={status}
                        disabled={!canManage}
                        onChange={(e) => setStatus(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="pf-pic">Picture URL</Label>
                      <Input
                        id="pf-pic"
                        value={picture}
                        placeholder="https://"
                        disabled={!canManage}
                        onChange={(e) => setPicture(e.target.value)}
                      />
                      {!pictureOk && <p className="text-xs text-red-600 mt-1">The picture URL must start with https://</p>}
                    </div>
                    {canManage && (
                      <Button size="sm" onClick={submitProfile} disabled={saveProfile.isPending || !pictureOk}>
                        {saveProfile.isPending ? 'Saving...' : 'Update profile'}
                      </Button>
                    )}
                  </div>
                </section>
              )}
            </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ---------------------------------------------------------------------------
// Page

type ConfirmKind = 'toggle' | 'restart' | 'logout' | 'delete';

const LinesPage: React.FC = () => {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useLinesQuery();
  const manage = useGate(PERMISSIONS.whatsapp.manageNumbers);
  const canManage = manage.allowed;

  const payload = data?.data;
  const lines = useMemo(() => payload?.lines ?? [], [payload]);

  const update = useUpdateLineMutation();
  const restart = useRestartLineMutation();
  const logout = useLogoutLineMutation();
  const remove = useDeleteLineMutation();

  const [addOpen, setAddOpen] = useState(false);
  const [created, setCreated] = useState<{ instance: string; label: string } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [linkInstance, setLinkInstance] = useState<string | null>(null);
  const [labelDlg, setLabelDlg] = useState<{ instance: string; mode: 'rename' | 'adopt' } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: ConfirmKind; instance: string } | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: LINES_KEY });
  const find = (id: string | null) => (id ? lines.find((l) => l.instance === id) ?? null : null);
  const selectedLine = find(selected);
  const linkLine = find(linkInstance);
  const labelLine = find(labelDlg?.instance ?? null);
  const confirmLine = find(confirm?.instance ?? null);

  const runConfirm = () => {
    if (!confirm || !confirmLine) return;
    const { instance } = confirm;
    const done = (msg: string) => ({
      onSuccess: () => {
        toast.success(msg);
        setConfirm(null);
        if (confirm.kind === 'delete') setSelected(null);
        refresh();
      },
      onError: (e: Error) => toastError(e, 'The action failed'),
    });
    switch (confirm.kind) {
      case 'toggle':
        update.mutate({ instance, enabled: !confirmLine.enabled }, done(confirmLine.enabled ? 'Sending turned off' : 'Sending turned on'));
        break;
      case 'restart':
        restart.mutate({ instance }, done('Line restarted'));
        break;
      case 'logout':
        logout.mutate({ instance }, done('Phone unlinked'));
        break;
      case 'delete':
        remove.mutate({ instance }, done('Line deleted'));
        break;
    }
  };

  const pending = update.isPending || restart.isPending || logout.isPending || remove.isPending;
  const server = payload?.server;
  const serverReachable = server ? server.reachable : payload?.reachable;

  const confirmCopy = (): { title: string; body: string; action: string } | null => {
    if (!confirmLine) return null;
    const n = lineName(confirmLine);
    switch (confirm?.kind) {
      case 'toggle':
        return confirmLine.enabled
          ? { title: 'Turn sending off?', body: `"${n}" will stop sending messages. The phone stays linked and keeps receiving.`, action: 'Turn off' }
          : { title: 'Turn sending on?', body: `"${n}" will start sending messages again.`, action: 'Turn on' };
      case 'restart':
        return { title: 'Restart line?', body: `"${n}" reconnects to WhatsApp. It may be unavailable for a few seconds.`, action: 'Restart' };
      case 'logout':
        return { title: 'Unlink phone?', body: `The phone ${formatPhone(confirmLine.phone) || ''} will be unlinked from "${n}". The line stays, and you can link a phone again later.`, action: 'Unlink phone' };
      default:
        return null;
    }
  };
  const copy = confirmCopy();

  const menu = (l: WhatsAppLine) => {
    const live = l.registered && l.exists;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Actions for ${lineName(l)}`}
            className="p-2 hover:bg-gray-100 rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="w-4 h-4 text-gray-600" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {!l.registered && l.exists && (
            <DropdownMenuItem onClick={() => setLabelDlg({ instance: l.instance, mode: 'adopt' })}>
              <UserPlus className="mr-2 h-4 w-4" /> Adopt
            </DropdownMenuItem>
          )}
          {live && l.state !== 'open' && (
            <DropdownMenuItem onClick={() => setLinkInstance(l.instance)}>
              <Link2 className="mr-2 h-4 w-4" /> Link phone
            </DropdownMenuItem>
          )}
          {live && (
            <DropdownMenuItem onClick={() => setLabelDlg({ instance: l.instance, mode: 'rename' })}>
              <Pencil className="mr-2 h-4 w-4" /> Rename
            </DropdownMenuItem>
          )}
          {live && (
            <DropdownMenuItem onClick={() => setConfirm({ kind: 'toggle', instance: l.instance })}>
              <Power className="mr-2 h-4 w-4" /> {l.enabled ? 'Turn sending off' : 'Turn sending on'}
            </DropdownMenuItem>
          )}
          {live && (
            <DropdownMenuItem onClick={() => setConfirm({ kind: 'restart', instance: l.instance })}>
              <RotateCw className="mr-2 h-4 w-4" /> Restart
            </DropdownMenuItem>
          )}
          {live && l.state === 'open' && (
            <DropdownMenuItem onClick={() => setConfirm({ kind: 'logout', instance: l.instance })}>
              <Unlink className="mr-2 h-4 w-4" /> Unlink phone
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-red-600" onClick={() => setConfirm({ kind: 'delete', instance: l.instance })}>
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WhatsApp lines</h1>
            <p className="text-sm text-gray-500 mt-1">
              Phones linked to our Evolution server. Each line sends as that phone.
            </p>
          </div>
          {payload?.enabled && (
            <PermissionButton code={PERMISSIONS.whatsapp.manageNumbers} onClick={() => setAddOpen(true)}>
              <Plus /> Add line
            </PermissionButton>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        {isLoading && <p className="text-sm text-gray-500">Loading WhatsApp lines...</p>}

        {isError && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">
              Could not load WhatsApp lines: {(error as Error)?.message || 'unknown error'}
            </p>
          </div>
        )}

        {payload && !payload.enabled && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Linked lines are switched off</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              The Evolution integration is disabled on the server. Set <code className="font-mono">WHATSAPP_EVOLUTION_ENABLED</code>{' '}
              to turn it on. Until then there is nothing to manage here.
            </p>
          </div>
        )}

        {payload?.enabled && (
          <>
            <ServerPanel />
            {server && !server.reachable && (
              <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-800">
                  <p className="font-semibold">The Evolution server cannot be reached.</p>
                  {errText(server.error || payload.error) && <p className="mt-1">{errText(server.error || payload.error)}</p>}
                </div>
              </div>
            )}
            {server && server.reachable && !server.licensed && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-900">
                  The Evolution server is not activated yet. Activate it once from the server&apos;s manager (see ops
                  runbook).
                </p>
              </div>
            )}
            {server && server.reachable && server.licensed && (
              <p className="flex items-center gap-1.5 text-xs text-gray-500">
                <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                Evolution {server.version || ''}
              </p>
            )}
            {!server && serverReachable === false && (
              <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">
                  The Evolution server cannot be reached. {errText(payload.error)}
                </p>
              </div>
            )}

            {lines.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No lines yet</h3>
                <p className="text-sm text-gray-500">Add a line, then link a phone to it.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Line</th>
                      <th className="px-4 py-3 font-medium">Phone</th>
                      <th className="px-4 py-3 font-medium">Profile</th>
                      <th className="px-4 py-3 font-medium">State</th>
                      <th className="px-4 py-3 font-medium">Since</th>
                      <th className="px-4 py-3 font-medium">Sending</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lines.map((l) => (
                      <tr
                        key={l.instance}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelected(l.instance)}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{lineName(l)}</div>
                          <div className="font-mono text-xs text-gray-500">{l.instance}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{formatPhone(l.phone) || 'Not linked'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Avatar line={l} />
                            <span className="text-gray-700">{l.profile_name || ''}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill line={l} />
                        </td>
                        <td className="px-4 py-3 text-gray-500">{l.state_at ? moment.unix(l.state_at).fromNow() : ''}</td>
                        <td className="px-4 py-3">
                          {l.registered ? (
                            <span className={l.enabled ? 'text-green-700' : 'text-gray-500'}>{l.enabled ? 'On' : 'Off'}</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right">{canManage && menu(l)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {payload?.notice && (
          <p className="flex items-start gap-1.5 text-xs text-gray-500 pt-4">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{payload.notice}</span>
          </p>
        )}
      </div>

      <AddLineDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(instance, label) => {
          setAddOpen(false);
          setCreated({ instance, label });
          refresh();
        }}
      />

      <AlertDialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Link a phone now?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{created?.label}&quot; is ready. Link a phone to start using it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Later</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (created) setLinkInstance(created.instance);
                setCreated(null);
              }}
            >
              Link phone
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LabelDialog
        line={labelLine}
        mode={labelDlg?.mode ?? 'rename'}
        onClose={() => setLabelDlg(null)}
        onDone={() => {
          setLabelDlg(null);
          refresh();
        }}
      />

      <LinkDialog
        line={linkLine}
        onClose={() => setLinkInstance(null)}
        onLinked={() => {
          setLinkInstance(null);
          refresh();
        }}
      />

      <DetailsDrawer
        line={selectedLine}
        canManage={canManage}
        onClose={() => setSelected(null)}
        onLink={(l) => setLinkInstance(l.instance)}
        onChanged={refresh}
      />

      <AlertDialog open={!!copy} onOpenChange={(o) => !o && !pending && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{copy?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending}
              onClick={(e) => {
                e.preventDefault();
                runConfirm();
              }}
            >
              {pending ? 'Working...' : copy?.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDeleteDialog
        open={confirm?.kind === 'delete' && !!confirmLine}
        onOpenChange={(o) => !o && !pending && setConfirm(null)}
        title="Delete line"
        itemLabel={confirmLine ? lineName(confirmLine) : ''}
        description="The phone gets unlinked from WhatsApp and the line disappears from this list. This cannot be undone."
        onConfirm={runConfirm}
        isPending={remove.isPending}
        errorMessage={remove.error?.message}
      />
    </div>
  );
};

export default LinesPage;
